/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useImpostazioni, urlImmagine } from '../hooks/useImpostazioni';
import { Home, FileText, Plus, LogOut, Package, Settings, Boxes } from 'lucide-react';
import NotificheBell from './NotificheBell';
import { useViewport } from '../hooks/useViewport';
import { useIsAzienda } from '../hooks/useProfilo';

const ACCENT = '#3B82F6';

const NAV = [
  { to: '/dashboard', label: 'Home', icon: Home },
  { to: '/interventi', label: 'Interventi', icon: FileText },
  { to: '/prodotti', label: 'Prodotti', icon: Package },
  { to: '/nuovo', label: 'Richiesta', icon: Plus },
];

// L'inventario ce l'hanno solo le aziende: al privato la voce non compare
// nemmeno, invece di portarlo a una pagina che gli dice di no.
const VOCE_INVENTARIO = { to: '/inventario', label: 'Inventario', icon: Boxes };

export default function ClienteLayout({ children, title }) {
  const { user, logout } = useAuth();
  const impostazioni = useImpostazioni();
  const logo = urlImmagine(impostazioni.app_logo_url);
  const location = useLocation();
  const { isCompact } = useViewport();
  const isAzienda = useIsAzienda();
  // Inserito prima di "Richiesta", che resta l'ultima voce anche nella tab bar.
  const voci = isAzienda ? [...NAV.slice(0, 3), VOCE_INVENTARIO, NAV[3]] : NAV;

  const isActive = (to) => location.pathname === to || (to !== '/dashboard' && location.pathname.startsWith(to));

  return (
    <div style={styles.wrapper}>
      <header style={styles.header}>
        <div style={{ ...styles.headerInner, ...(isCompact ? styles.headerInnerCompact : {}) }}>
          <div style={styles.brand}>
            {logo
              ? <img src={logo} alt="" style={styles.brandLogo} />
              : <div style={{ ...styles.brandDot, backgroundColor: ACCENT }} />}
            <div>
              <div style={styles.brandTitle}>{impostazioni.app_nome}</div>
              <div style={styles.brandSub}>Area Cliente</div>
            </div>
          </div>
          {/* Nav orizzontale solo su desktop; su mobile/tablet sta nella bottom bar */}
          {!isCompact && (
            <nav style={styles.nav}>
              {voci.map(({ to, label, icon: Icon }) => (
                <Link key={to} to={to} style={{ ...styles.navLink, ...(isActive(to) ? { color: ACCENT, fontWeight: 600 } : {}) }}>
                  <Icon size={18} /> <span>{label}</span>
                </Link>
              ))}
            </nav>
          )}
          <div style={styles.userArea}>
            <NotificheBell />
            {!isCompact && <span style={styles.userName}>{user?.nome} {user?.cognome}</span>}
            <Link to="/account" style={styles.accountBtn} title="Il mio account"><Settings size={18} /></Link>
            <button onClick={logout} style={styles.logoutBtn} title="Esci"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <main style={{ ...styles.main, ...(isCompact ? styles.mainCompact : {}) }}>
        {title && <h1 style={{ ...styles.pageTitle, ...(isCompact ? styles.pageTitleCompact : {}) }}>{title}</h1>}
        {children}
        <div style={styles.footer}>
          <Link to="/dicci-la-tua" style={styles.footerLink}>Dicci la tua</Link>
          <span style={styles.footerLink}> · </span>
          <Link to="/privacy" style={styles.footerLink}>Informativa Privacy</Link>
        </div>
      </main>

      {/* Bottom tab bar: solo su mobile/tablet */}
      {isCompact && (
        <nav style={styles.bottomNav}>
          {voci.map(({ to, label, icon: Icon }) => {
            const active = isActive(to);
            return (
              <Link key={to} to={to} style={{ ...styles.bottomLink, ...(active ? { color: ACCENT } : {}) }}>
                <Icon size={22} />
                <span style={styles.bottomLabel}>{label}</span>
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
}

const styles = {
  wrapper: { minHeight: '100vh', backgroundColor: '#F9FAFB' },
  header: { backgroundColor: 'white', borderBottom: '1px solid #E5E7EB', position: 'sticky', top: 0, zIndex: 20 },
  headerInner: { maxWidth: '1000px', margin: '0 auto', padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' },
  headerInnerCompact: { padding: '0.7rem 1rem', gap: '0.75rem', justifyContent: 'space-between' },
  brand: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  brandDot: { width: '12px', height: '12px', borderRadius: '4px' },
  brandLogo: { width: '28px', height: '28px', objectFit: 'contain', flexShrink: 0 },
  brandTitle: { fontWeight: 700, fontSize: '0.95rem' },
  brandSub: { fontSize: '0.72rem', color: '#6B7280' },
  nav: { display: 'flex', gap: '0.5rem', flex: 1, flexWrap: 'wrap' },
  navLink: { display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.8rem', borderRadius: '8px', color: '#374151', textDecoration: 'none', fontSize: '0.9rem' },
  userArea: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  userName: { fontSize: '0.88rem', color: '#374151', fontWeight: 500 },
  logoutBtn: { padding: '0.5rem', backgroundColor: '#FEE2E2', color: '#991B1B', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex', minWidth: '40px', minHeight: '40px', alignItems: 'center', justifyContent: 'center' },
  accountBtn: { padding: '0.5rem', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex', minWidth: '40px', minHeight: '40px', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' },
  footer: { marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #E5E7EB', textAlign: 'center' },
  footerLink: { color: '#9CA3AF', fontSize: '0.8rem', textDecoration: 'none' },
  main: { maxWidth: '1000px', margin: '0 auto', padding: '1.5rem 1.25rem' },
  mainCompact: { padding: '1rem 1rem calc(72px + 1rem)' },
  pageTitle: { fontSize: '1.5rem', fontWeight: 700, margin: '0 0 1.25rem' },
  pageTitleCompact: { fontSize: '1.2rem', margin: '0 0 1rem' },
  bottomNav: { position: 'fixed', bottom: 0, left: 0, right: 0, height: '64px', backgroundColor: 'white', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-around', alignItems: 'stretch', zIndex: 30, boxShadow: '0 -1px 6px rgba(0,0,0,0.05)' },
  bottomLink: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.2rem', color: '#6B7280', textDecoration: 'none', fontSize: '0.7rem', minHeight: '44px' },
  bottomLabel: { fontSize: '0.68rem', fontWeight: 600 },
};
